import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from '@react-router';
import { useAuth } from '@shared/components/AuthProvider';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useSessionPersistedState } from '@shared/hooks/usePersistedState';
import { CultureCode, resolveEventLocale, updateLocaleLocalStorage, useGuestGeoLocale } from '@shared/hooks/useGeoLocale';
import { buildAuthUrl, performFullPageRedirect } from '@shared/utils/navigation';
import { useEffectOnce } from '@shared/utils/hooks/useEffectOnce';
import { useIsMounted } from '@shared/utils/hooks/useIsMounted';
import { useMount } from '@shared/utils/hooks/useMount';
import { useIsOutsideUsDeliveryRange } from '@apps/registry/common/hooks/useIsOutsideUsDeliveryRange';
import { withWindow } from '@shared/utils/withWindow';
import { usePublicShopTelemetry } from '../../Shop.telemetry';
import { useIsIntlSupportedCatalogPath } from './useIsIntlSupportedCatalogPath';
import { CASH_FUNDS_PATH, SHOP_REGION_DIALOG_DISMISSED_KEY, SHOP_REGION_DIALOG_RESUME_PARAM, buildIntlAddGiftsPath } from './ShopRegionDialog.constants';
import type { GoToDestinationOptions, PendingEventChoice, ShopRegionDialogDestination, ShopRegionDialogGateArgs } from './ShopRegionDialog.types';

export const useShopRegionDialogGate = ({
  registryShippingAddress,
  intlEventHandles,
  isResolvingShopContext,
  selectedEventHandle,
  showEventPickerDialog,
}: ShopRegionDialogGateArgs) => {
  const { isLoggedIn } = useAuth();
  const hasMounted = useIsMounted();
  const isOutsideUsRegion = useIsOutsideUsDeliveryRange(registryShippingAddress);
  const { isResolved: isCatalogScopeResolved, isIntlSupported: isIntlSupportedCategory } = useIsIntlSupportedCatalogPath();
  const [isDismissed, setIsDismissed] = useSessionPersistedState<boolean>(SHOP_REGION_DIALOG_DISMISSED_KEY, false);
  // Holds the handle selected at the moment we opened the picker, so only a *change* counts as a
  // choice. A shopper who already had an international event selected still gets to see the picker.
  const [pendingEventChoice, setPendingEventChoice] = useState<PendingEventChoice | null>(null);
  const { getValueString, buildRedirectPath } = useQueryParamHelper();
  const location = useLocation();
  const history = useHistory();

  const dismiss = useCallback(() => setIsDismissed(true), [setIsDismissed]);

  const goToIntlAddGifts = useCallback((eventHandle: string, shouldReplace: boolean = false) => performFullPageRedirect(buildIntlAddGiftsPath(eventHandle), shouldReplace), []);

  const resolveDestination = useCallback((): ShopRegionDialogDestination => {
    if (intlEventHandles.length === 1) return 'intlAddGifts';
    if (intlEventHandles.length > 1) return 'eventPicker';
    return isLoggedIn ? 'cashFunds' : 'login';
  }, [intlEventHandles, isLoggedIn]);

  const goToDestination = useCallback(
    (destination: ShopRegionDialogDestination, { shouldReplace = false }: GoToDestinationOptions = {}) => {
      switch (destination) {
        case 'intlAddGifts':
          return goToIntlAddGifts(intlEventHandles[0], shouldReplace);
        case 'eventPicker':
          setPendingEventChoice({ baselineHandle: selectedEventHandle });
          return showEventPickerDialog();
        case 'login':
          return withWindow(global => {
            global.location.href = buildAuthUrl('/login', buildRedirectPath(`${location.pathname}${location.search}`, { [SHOP_REGION_DIALOG_RESUME_PARAM]: '1' }));
          });
        case 'cashFunds':
          return shouldReplace ? history.replace(CASH_FUNDS_PATH) : history.push(CASH_FUNDS_PATH);
      }
    },
    [intlEventHandles, goToIntlAddGifts, showEventPickerDialog, selectedEventHandle, buildRedirectPath, location.pathname, location.search, history],
  );

  const isResuming = getValueString(SHOP_REGION_DIALOG_RESUME_PARAM) === '1';
  const isSettled = hasMounted && !isResolvingShopContext;

  // Replace, don't push: the resume URL still carries intlGiftList=1, so a Back press onto it would
  // re-run this effect and throw the shopper forward again.
  useEffectOnce(isResuming && isSettled, () => {
    const destination = resolveDestination();
    goToDestination(destination === 'login' ? 'cashFunds' : destination, { shouldReplace: true });
  });

  // Consumed by the first selection change after we opened the picker, whatever it is. Picking a US
  // event disarms it, so a later international selection the dialog never asked for can't redirect.
  useEffect(() => {
    if (!pendingEventChoice || selectedEventHandle === pendingEventChoice.baselineHandle) return;

    setPendingEventChoice(null);
    if (selectedEventHandle && intlEventHandles.includes(selectedEventHandle)) {
      goToIntlAddGifts(selectedEventHandle);
    }
  }, [pendingEventChoice, selectedEventHandle, intlEventHandles, goToIntlAddGifts]);

  useEffect(() => setPendingEventChoice(null), [location.pathname]);

  // Cash Funds and Charity ship worldwide, so the region hand-off has nothing to offer on those trees.
  const isPromptableCategory = isCatalogScopeResolved && !isIntlSupportedCategory;

  // isResuming is in the skip too: that load redirects immediately, so counting it as an exposure
  // would inflate the denominator with shoppers who never saw the dialog.
  const [isDialogEnabled] = useIsFeatureEnabled('registryShopRegionDialogEnabled', { skip: !isSettled || !isOutsideUsRegion || !isPromptableCategory || isResuming });

  return {
    isVisible: isSettled && isPromptableCategory && isDialogEnabled && !isDismissed && !isResuming && isOutsideUsRegion,
    dismiss,
    resolveDestination,
    goToDestination,
  };
};

export type ShopRegionDialogContentArgs = Omit<ReturnType<typeof useShopRegionDialogGate>, 'isVisible'>;

export const useShopRegionDialogContent = ({ dismiss, resolveDestination, goToDestination }: ShopRegionDialogContentArgs) => {
  const { languageCodeIso15897, countryName } = useGuestGeoLocale();
  const [detectedLocale] = useState<string>(languageCodeIso15897);
  const [selectedLocale, setSelectedLocale] = useState<string>(languageCodeIso15897);
  const { shopRegionDialog, shopRegionDialogContinue, shopRegionDialogExploreCashFunds, shopRegionDialogLocaleChanged, shopRegionDialogDismissed } = usePublicShopTelemetry();

  useMount(() => {
    shopRegionDialog.enter(detectedLocale);
  });

  const onLocaleChange = useCallback(
    (nextLocale: string) => {
      const resolved = resolveEventLocale(nextLocale);
      if (!resolved) return;

      setSelectedLocale(nextLocale);
      updateLocaleLocalStorage(resolved);
      shopRegionDialogLocaleChanged({ detectedLocale, selectedLocale: nextLocale });

      if (resolved.culture === CultureCode.US) {
        dismiss();
      }
    },
    [detectedLocale, dismiss, shopRegionDialogLocaleChanged],
  );

  const onContinue = useCallback(() => {
    const destination = resolveDestination();
    shopRegionDialogContinue({ detectedLocale, selectedLocale, destination });
    dismiss();
    goToDestination(destination);
  }, [resolveDestination, goToDestination, dismiss, shopRegionDialogContinue, detectedLocale, selectedLocale]);

  const onExploreCashFunds = useCallback(() => {
    shopRegionDialogExploreCashFunds({ detectedLocale, selectedLocale });
    dismiss();
    goToDestination('cashFunds');
  }, [detectedLocale, selectedLocale, shopRegionDialogExploreCashFunds, dismiss, goToDestination]);

  const onClose = useCallback(() => {
    shopRegionDialogDismissed({ detectedLocale, selectedLocale });
    dismiss();
  }, [detectedLocale, selectedLocale, shopRegionDialogDismissed, dismiss]);

  return { countryName, selectedLocale, onLocaleChange, onContinue, onExploreCashFunds, onClose };
};
